import { cn } from '@/lib/utils';
import { InlineToast } from '@chiroup/components/feedback';
import { SmsConsentStatus } from '@chiroup/core/enums/SmsConsentStatus.enum';
import { convertToMS } from '@chiroup/core/functions/convertToMS';
import { scrollToId } from '@chiroup/core/functions/scrollToId';
import { HealthComLocation } from '@chiroup/core/types/HealthComLocation.type';
import {
  ArrowDownTrayIcon,
  CalendarDaysIcon,
  ExclamationTriangleIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import * as Sentry from '@sentry/react';
import dayjs, { Dayjs } from 'dayjs';
import tz from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useEffect, useMemo, useRef, useState } from 'react';
import ReactGA from 'react-ga4';
import { useNavigate } from 'react-router-dom';
import { usePatientPortal } from '../contexts/PatientPortalContext';
import useLocation from '../hooks/useLocation';
import appointmentService from '../services/appointment.service';
import locationService, {
  type AvailabilityResponse,
  type AvailabilitySlot,
} from '../services/location.service';
import smsOptinService from '../services/smsOptin.service';
import { downloadAppointmentIcs } from '../utils/buildAppointmentIcs';
import { ScheduleForm } from './Container';
import { VerifiedGuestContact } from './EmailOrPhoneLogin';
import NotSetup from './NotSetup';
import ScheduleConfirm from './ScheduleConfirm';
import ScheduleLocations from './ScheduleLocations';
import ScheduleProviders from './ScheduleProviders';
import ScheduleSkeleton from './ScheduleSkeleton';
import ScheduleTime from './ScheduleTime';
import ScheduleTreatments from './ScheduleTreatments';
// Custom auth replaces Cognito. Local stand-in for the AuthUser
// shape the component used to consume.
type AuthUser = { userId: string; username: string };

type DejavooPaymentConfigFailure = {
  reason: 'empty_response' | 'request_error';
  clinicId?: number;
  mode: 'portal' | 'public';
  error?: unknown;
};

const captureDejavooPaymentConfigFailure = ({
  reason,
  clinicId,
  mode,
  error,
}: DejavooPaymentConfigFailure): void => {
  Sentry.withScope(scope => {
    scope.setLevel('error');
    scope.setFingerprint(['healthcom-schedule', 'dejavoo-payment-config', reason]);
    scope.setTag('clinicId', clinicId ? String(clinicId) : 'unknown');
    scope.setTag('payment_processor', 'dejavoo');
    scope.setTag('payment_config_failure', reason);
    scope.setTag('booking_mode', mode);
    scope.setTag(
      'runtime',
      document.documentElement.classList.contains('capacitor-native') ? 'native' : 'web'
    );

    if (error) {
      Sentry.captureException(error);
    } else {
      Sentry.captureMessage('Dejavoo payment configuration unavailable');
    }
  });
};

dayjs.extend(utc);
dayjs.extend(tz);

const today = dayjs();
const startOfMonth = today.startOf('month');
const normalizePhone = (phone?: string | null) => (phone || '').replace(/\D/g, '');

interface AvailableTimesType {
  providerId: string;
  providerName?: string;
  profileImage?: string | null;
  times: AvailabilitySlot[];
}

type Props = {
  setUser: React.Dispatch<React.SetStateAction<AuthUser | null>>;
  user: AuthUser | null;
  clinicLocationData?: HealthComLocation;
  path: string;
  getMe: (force?: boolean) => Promise<void>;
  values: ScheduleForm;
  setValues: React.Dispatch<React.SetStateAction<ScheduleForm>>;
  me: {
    patients: any[];
  } | null;
  /**
   * `'portal'` (default) — running inside Container's authed shell.
   * Reads selected-patient and available-locations from the portal
   * context, lazily calls `getMe()` to populate payment/SMS-consent
   * state on first mount.
   *
   * `'public'` — running inside `PublicSchedule` for unauthed visitors
   * coming in through `/clinic-schedule/<path>`. Skips the multi-
   * location step (the URL path identifies the location), skips the
   * `/me/{path}` prefetch (no Cognito session yet — the call would
   * 401), and lets `ScheduleConfirm` use its `<Login>` fallback to
   * authenticate the user as part of the booking step.
   */
  mode?: 'portal' | 'public';
};

const Schedule: React.FC<Props> = ({
  setUser,
  user: _user,
  clinicLocationData,
  path,
  getMe,
  me,
  values,
  setValues,
  mode = 'portal',
}) => {
  const navigate = useNavigate();
  const isPublic = mode === 'public';
  const { selectedPatient: portalPatient } = usePatientPortal();
  // Active locations for the clinic, exposed by mePortal. The Schedule
  // flow gets a Location step (rendered as Step 1) only when the clinic
  // operates from more than one of these — single-location clinics keep
  // the existing 3-step flow. In public mode the URL path already
  // identifies the location, so the Location step is always skipped.
  const availableLocations = useMemo(
    () => (portalPatient?.clinic?.availableLocations || []).filter(loc => !!loc.path),
    [portalPatient?.clinic?.availableLocations]
  );
  const isMultiLocation = !isPublic && availableLocations.length > 1;

  // Path of the location the user is booking at. For single-location
  // clinics this falls back to the prop `path` immediately. For
  // multi-location clinics it stays null until the patient picks one in
  // Step 1, which forces the rest of the wizard to wait.
  const [selectedLocationPath, setSelectedLocationPath] = useState<string | null>(
    isMultiLocation ? null : path
  );
  const effectivePath = selectedLocationPath || (isMultiLocation ? '' : path);
  // Refetch location data (providers, disciplines, primaryColor, …) when
  // the user picks a different location. react-query reuses the cache
  // when the path matches what Container already prefetched, so the
  // single-location path doesn't pay an extra request.
  const { data: pickedLocationData } = useLocation(selectedLocationPath || null);
  const effectiveLocationData = isMultiLocation
    ? pickedLocationData || (selectedLocationPath ? undefined : clinicLocationData)
    : clinicLocationData;

  const [selectedDate, setSelectedDate] = useState<Dayjs>();
  const [viewDate, setViewDate] = useState(startOfMonth);
  const [isCreditCardRequired, setIsCreditCardRequired] = useState(false);
  const [epayPublicKey, setEpayPublicKey] = useState<string | null>(null);
  const [processor, setProcessor] = useState<'usaepay' | 'dejavoo' | null>(null);
  const [dejavooEcommToken, setDejavooEcommToken] = useState<string | null>(null);
  // Stash the tokenization result so Schedule's confirm() can ship it to the
  // backend alongside the appointment payload. ScheduleConfirm exposes a
  // tokenize() ref that resolves to { payment_token_id, ...cardData }.
  const dejavooCardEntryRef = useRef<{
    tokenize: () => Promise<{
      paymentTokenId: string;
      cardData: {
        cardholderFirstName: string;
        cardholderLastName: string;
        postalCode: string;
        cardLastFour: string;
        cardType: string;
      };
    } | null>;
  } | null>(null);
  const [availability, setAvailability] = useState<AvailabilityResponse>();
  const [dottedDays, setDottedDays] = useState<{
    [key: number]: boolean;
  }>({});
  const [availableTimes, setAvailableTimes] = useState<AvailableTimesType[]>([]);
  const [selectedDay, setSelectedDay] = useState<string>();
  const [loadingAvailability, setLoadingAvailability] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [calendarToast, setCalendarToast] = useState(false);
  const calendarToastTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(calendarToastTimer.current), []);
  const [appointmentSettings, setAppointmentSettings] = useState({
    requireCreditCard: false,
  });
  /**
   * Populated after a successful booking so we can render the inline
   * confirmation view instead of navigating to /thank-you. "Book
   * another" resets this to null and drops the user back into the
   * fresh scheduling flow.
   */
  const [bookedAppointment, setBookedAppointment] = useState<{
    treatmentName: string;
    providerName: string;
    providerImage?: string;
    appointmentDate: string;
    appointmentTime: string;
    patientName?: string;
    // Raw fields used to build the ICS download. Captured separately
    // from the formatted display strings so we don't have to reparse.
    appointmentId: string;
    startTimestamp: number;
    durationMinutes: number;
    location: HealthComLocation | null;
  } | null>(null);

  // Verified-but-no-Patient state. Populated by ScheduleConfirm when
  // the OTP flow proved ownership of a contact that doesn't have a
  // Patient row yet. When set, the booking call routes through
  // `appointmentService.createGuest` (which creates a pending Patient
  // + appointment + portal session in one shot) instead of the
  // session-required `appointmentService.create`.
  const [verifiedGuest, setVerifiedGuest] = useState<VerifiedGuestContact | null>(null);

  // Lazy-load `/me/{path}` (payment methods, SMS consent, patient phone) when
  // the schedule flow is first opened. Container no longer fetches this
  // eagerly — most portal sessions never reach this route, so deferring
  // saves the call on every Home/HealthHub/Exercises pageload.
  //
  // The ref gate is load-bearing: Container's `getMe` swallows non-503
  // errors and calls `setMe(undefined)`. Without the gate, a single API
  // failure would flip `me` from `null` → `undefined` → effect re-fires
  // → getMe called again → tight refetch loop (we observed >300
  // requests/sec). One attempt per mount is enough; if it fails, the
  // user can navigate away and back to retry.
  const meFetchAttemptedRef = useRef(false);

  useEffect(() => {
    // Public mode: no Cognito session yet, /me/{path} would 401. The
    // user authenticates inside ScheduleConfirm's <Login> fallback at
    // book time; getMe() runs from there once they're signed in.
    if (isPublic) return;
    if (meFetchAttemptedRef.current) return;
    if (me?.patients) return;
    meFetchAttemptedRef.current = true;
    getMe();
  }, [isPublic, me, getMe]);

  useEffect(() => {
    // Auto-select patient: if a portal patient is already chosen and exists in me.patients, use it
    if (portalPatient?.ID && me?.patients?.length) {
      const match = me.patients.find(p => p.ID === portalPatient.ID);

      if (match) {
        setValues(prev => ({
          ...prev,
          patient: match.ID,
        }));

        return;
      }
    }

    // Fallback: auto-select if only one patient
    if (me?.patients?.length === 1) {
      setValues(prev => ({
        ...prev,
        patient: me?.patients[0]?.ID,
      }));
    }
  }, [me?.patients, portalPatient?.ID, setValues]);

  const patientSelected = useMemo(() => {
    if (values.patient) {
      return me?.patients?.find(p => p.ID === values.patient);
    }

    return null;
  }, [values.patient, me?.patients]);

  useEffect(() => {
    if (!values.patient || !patientSelected) return;

    if (patientSelected.phone && values.phone !== patientSelected.phone) {
      setValues(prev => ({
        ...prev,
        phone: patientSelected.phone,
      }));
    }

    if (
      patientSelected.smsConsent &&
      (values.smsConsent === null || values.smsConsent === undefined)
    ) {
      setValues(prev => ({
        ...prev,
        smsConsent: patientSelected.smsConsent,
      }));
    }
  }, [patientSelected, setValues, values.patient, values.phone, values.smsConsent]);

  const selectedPatientHasPaymentMethod = useMemo(() => {
    return !!patientSelected?.hasPaymentMethod;
  }, [patientSelected]);

  useEffect(() => {
    if (!appointmentSettings?.requireCreditCard) {
      setIsCreditCardRequired(false);
    } else {
      setIsCreditCardRequired(!selectedPatientHasPaymentMethod);
    }
  }, [selectedPatientHasPaymentMethod, appointmentSettings?.requireCreditCard, patientSelected]);

  // For Dejavoo, the manual-entry merchant follows the selected patient's
  // assigned location (primary fallback). Fetch that patient-specific ecommToken
  // once a patient is chosen and tokenize with it, so the browser tokenizes with
  // the same merchant the backend will later charge.
  //
  // We reach here only when the clinic requires a card AND the patient has none
  // on file, and the backend enforces that unconditionally: a tokenless Dejavoo
  // booking is always rejected with "Payment method is required for this
  // appointment." (appointmentCreate.ts) — it demands the token *before* it even
  // resolves the device. So this effect must NEVER drop the requirement.
  // Previously a failed/empty fetch set isCreditCardRequired(false), which hid
  // the card UI yet still submitted tokenless → the patient saw the required
  // error with no way to add a card (observed on Android / in-app browsers where
  // the authed request can fail). Instead: got a token → show the form; no token
  // → keep the requirement and let ScheduleConfirm surface its "Unable to load
  // the payment form" state so the patient can retry / open a real browser.
  useEffect(() => {
    if (processor !== 'dejavoo') return;
    if (!appointmentSettings?.requireCreditCard) return;
    if (selectedPatientHasPaymentMethod) return;
    if (!values.patient) return;

    let cancelled = false;

    (async () => {
      try {
        const config = await locationService.getDejavooManualConfig({
          path: effectivePath,
          patientId: values.patient as string,
        });

        if (cancelled) return;

        setIsCreditCardRequired(true);
        setDejavooEcommToken(config?.ecommToken ?? null);

        if (!config?.ecommToken) {
          captureDejavooPaymentConfigFailure({
            reason: 'empty_response',
            clinicId: effectiveLocationData?.clinicId,
            mode,
          });
        }
      } catch (configError) {
        if (!cancelled) {
          captureDejavooPaymentConfigFailure({
            reason: 'request_error',
            clinicId: effectiveLocationData?.clinicId,
            mode,
            error: configError,
          });
          setIsCreditCardRequired(true);
          setDejavooEcommToken(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    processor,
    appointmentSettings?.requireCreditCard,
    selectedPatientHasPaymentMethod,
    values.patient,
    effectivePath,
    effectiveLocationData?.clinicId,
    mode,
  ]);

  const getAvailability = async (
    provider: string | null,
    treatment: number | null,
    year: string,
    month: string
  ) => {
    if (!effectivePath) return;
    setLoadingAvailability(true);
    const { formattedAvailability, appointmentSettings: fetchedSettings } =
      await locationService.getAvailability({
        path: effectivePath,
        provider,
        treatment,
        year,
        month,
        timezone: effectiveLocationData?.timezone as string,
      });

    if (fetchedSettings?.epayPublicKey) {
      setEpayPublicKey(fetchedSettings.epayPublicKey);
    }

    if (fetchedSettings?.processor) {
      setProcessor(fetchedSettings.processor);
    }

    if (fetchedSettings?.dejavoo?.ecommToken) {
      setDejavooEcommToken(fetchedSettings.dejavoo.ecommToken);
    }

    setIsCreditCardRequired(!!fetchedSettings?.requireCreditCard);
    setAppointmentSettings(fetchedSettings);

    setAvailability(formattedAvailability);
    setDottedDays(
      Object.entries(formattedAvailability).reduce(
        (
          acc: {
            [key: number]: boolean;
          },
          [date, dayAvailability]
        ) => {
          const day = dayjs(date).date();
          const isAvailable = Object.values(dayAvailability).some(times => times.length);

          if (isAvailable) {
            acc[day] = true;
          }

          return acc;
        },
        {}
      )
    );

    setLoadingAvailability(false);
  };

  const confirm = async () => {
    try {
      const submitThis: ScheduleForm = { ...values };

      setError(null);
      setIsSaving(true);

      try {
        if (
          submitThis.phone &&
          submitThis.smsConsent === SmsConsentStatus.OPTED_IN &&
          !hasPhoneConsent
        ) {
          await smsOptinService.optInTransactional({
            clinicPath: effectivePath,
            phoneNumber: submitThis.phone,
          });
        }

        // Branch the create call on verifiedGuest: an OTP-verified
        // visitor without a Patient row goes through /appointment-guest
        // (creates pending Patient + appointment + portal session in
        // one shot); everyone else uses the authed /appointment.
        const guestBookingPayload = verifiedGuest
          ? {
              challengeId: verifiedGuest.challengeId,
              path: effectivePath,
              fname: (submitThis.fname || '').trim(),
              lname: (submitThis.lname || '').trim(),
              dateOfBirth: submitThis.dateOfBirth || '',
              treatment: submitThis.treatment as number,
              provider: submitThis.provider as string,
              date: submitThis.date as string,
              startTime: submitThis.startTime,
              endTime: submitThis.endTime,
              email:
                verifiedGuest.contactType === 'email'
                  ? verifiedGuest.contact
                  : submitThis.email || null,
              phone:
                verifiedGuest.contactType === 'phone'
                  ? verifiedGuest.contact
                  : submitThis.phone || null,
            }
          : null;

        if (isCreditCardRequired && processor === 'dejavoo') {
          // Dejavoo manual entry: trigger the FTD SDK to tokenize the card
          // the user entered in ScheduleConfirm. Await the result so we don't
          // create the appointment without a valid token. onError is handled
          // inside the card entry component.
          if (!dejavooCardEntryRef.current) {
            throw new Error('Payment form not ready. Please try again.');
          }

          const tokenResult = await dejavooCardEntryRef.current.tokenize();

          if (!tokenResult?.paymentTokenId) {
            // DejavooCardEntry already surfaced the error via onError; abort.
            setIsSaving(false);

            return;
          }

          const dejavooPayment = {
            dejavooPaymentTokenId: tokenResult.paymentTokenId,
            dejavooCardLastFour: tokenResult.cardData.cardLastFour,
            dejavooCardType: tokenResult.cardData.cardType,
            dejavooCardholderName:
              `${tokenResult.cardData.cardholderFirstName} ${tokenResult.cardData.cardholderLastName}`.trim(),
          };

          if (guestBookingPayload) {
            await appointmentService.createGuest(guestBookingPayload, undefined, dejavooPayment);
          } else {
            await appointmentService.create(
              {
                ...submitThis,
                path: effectivePath,
              },
              undefined,
              dejavooPayment
            );
          }
        } else if (isCreditCardRequired) {
          if ((window as any).usaepay) {
            const client = new (window as any).usaepay.Client(epayPublicKey);
            const paymentCard = client.createPaymentCardEntry();

            const result = await client.getPaymentKey(paymentCard);

            if (result.error) {
              throw new Error(result.error);
            } else if (guestBookingPayload) {
              await appointmentService.createGuest(guestBookingPayload, result.key);
            } else {
              await appointmentService.create(
                {
                  ...submitThis,
                  path: effectivePath,
                },
                result.key
              );
            }
          }
        } else if (guestBookingPayload) {
          await appointmentService.createGuest(guestBookingPayload);
        } else {
          await appointmentService.create({
            ...submitThis,
            path: effectivePath,
          });
        }

        if (effectiveLocationData?.googleAnalyticsTrackingId) {
          ReactGA.event({
            category: 'scheduling',
            action: 'scheduled_appointment',
          });
        }

        await getMe();

        // Snapshot the booked details BEFORE resetting `values` so the
        // inline confirmation view has something to render.
        const bookedPatient = submitThis.patient
          ? me?.patients?.find((p: any) => p.ID === submitThis.patient)
          : null;

        // Treatment duration drives the ICS event's end time. We
        // don't get a created appointmentId back from `create()` —
        // synthesizing one is fine since ICS UIDs only need to be
        // unique within the calendar, not match a DB record.
        const treatmentForBooking = allTreatments?.find((t: any) => t.ID === submitThis.treatment);
        const startMs =
          submitThis.startTime != null ? convertToMS(Number(submitThis.startTime)) : 0;

        setBookedAppointment({
          treatmentName,
          providerName: providerSelected?.name || '',
          providerImage: providerSelected?.profileImage || undefined,
          appointmentDate: dayjs
            .tz(submitThis.date, effectiveLocationData?.timezone)
            .format('MMMM D, YYYY'),
          appointmentTime,
          patientName: bookedPatient
            ? `${bookedPatient.fname || ''} ${bookedPatient.lname || ''}`.trim()
            : submitThis.fname && submitThis.lname
              ? `${submitThis.fname} ${submitThis.lname}`
              : undefined,
          appointmentId: `booked-${startMs}-${submitThis.treatment ?? ''}`,
          startTimestamp: startMs,
          durationMinutes: Number(treatmentForBooking?.duration) || 30,
          location: effectiveLocationData ?? null,
        });

        setValues((prev: any) => ({
          ...prev,
          date: null,
          startTime: null,
          endTime: null,
          treatment: null,
          provider: null,
          time: null,
          patient: null,
          fname: null,
          lname: null,
          email: null,
          phone: null,
          dateOfBirth: null,
        }));

        // The guest flow just finished minting a real portal session
        // for the now-existing Patient row. Clear the verified-guest
        // marker so a subsequent "Book another" doesn't accidentally
        // re-trigger the guest path against a stale challenge.
        if (verifiedGuest) setVerifiedGuest(null);
      } catch (e: any) {
        const err = typeof e === 'string' ? JSON.parse(e) : e;
        setError(err.response?.data?.code || err.response?.data?.message || err.message);
      }

      setIsSaving(false);
    } catch (err: any) {
      setIsSaving(false);
      setError(err.response?.data?.code);
    }
  };

  const providerSelected = useMemo(() => {
    return (
      effectiveLocationData?.providers?.find(p => p.ID === values.provider) || {
        name: '',
        profileImage: '',
      }
    );
  }, [effectiveLocationData?.providers, values.provider]);

  const allTreatments = useMemo(() => {
    return effectiveLocationData?.disciplines?.reduce((acc: any[], discipline: any) => {
      return [...acc, ...discipline.treatments];
    }, []);
  }, [effectiveLocationData?.disciplines]);

  const treatmentName = useMemo(() => {
    for (const discipline of effectiveLocationData?.disciplines || []) {
      for (const treatment of discipline.treatments) {
        if (treatment.ID === values.treatment) return treatment.name;
      }
    }

    return '';
  }, [effectiveLocationData?.disciplines, values.treatment]);

  const appointmentTime = useMemo(() => {
    return values.startTime && values.endTime
      ? `${dayjs
          .tz(convertToMS(Number(values.startTime)), effectiveLocationData?.timezone)
          .format('h:mma')} - ${dayjs
          .tz(convertToMS(Number(values.endTime)), effectiveLocationData?.timezone)
          .format('h:mma')}`
      : '';
  }, [effectiveLocationData?.timezone, values.endTime, values.startTime]);

  const hasPhoneConsent = useMemo(() => {
    if (!values.phone || !me?.patients?.length) return false;

    const normalized = normalizePhone(values.phone);

    return me.patients.some(
      (p: any) =>
        normalizePhone(p?.phone) === normalized && p?.smsConsent === SmsConsentStatus.OPTED_IN
    );
  }, [values.phone, me?.patients]);

  const providerInfo = (userId: string) => {
    return effectiveLocationData?.providers?.find((p: any) => p.ID === userId);
  };

  const providerOptions = useMemo(() => {
    const treatmentSelected = allTreatments?.find(t => t.ID === values.treatment);
    if (!treatmentSelected?.providers?.length) return effectiveLocationData?.providers || [];

    return (
      effectiveLocationData?.providers?.filter(p => {
        return treatmentSelected.providers.includes(p.ID);
      }) || []
    );
  }, [effectiveLocationData?.providers, values.treatment, allTreatments]);

  // Disabled when this clinic genuinely can't take bookings (no
  // providers / treatments configured). Skip the check while we're
  // still waiting for a multi-location user to pick a location, since
  // there's no location data yet.
  const cannotBook = useMemo(() => {
    // This switch is clinic-wide, so it can stop the flow before a
    // multi-location patient is asked to choose a location.
    if (effectiveLocationData?.allowOnlineScheduling === false) return true;
    // Multi-location, no pick yet — nothing to decide; the Location
    // picker carries the UI from here.
    if (isMultiLocation && !selectedLocationPath) return false;
    // Data hasn't arrived yet (e.g. just after picking a new location)
    // — don't flash NotSetup.
    if (!effectiveLocationData) return false;

    return !providerOptions.length || !allTreatments?.length;
  }, [
    providerOptions,
    allTreatments,
    isMultiLocation,
    selectedLocationPath,
    effectiveLocationData,
  ]);

  // Phone shown on the "online scheduling unavailable" screen. Sourced
  // from the patient's default (assigned) location — matched in
  // availableLocations by the snapshot's locationId — falling back to
  // the clinic snapshot phone, then the location currently being viewed
  // (covers public mode, where there's no signed-in patient). Null when
  // none is on file, which switches NotSetup to its generic message.
  const notSetupPhone = useMemo(() => {
    const clinic = portalPatient?.clinic;
    const assignedLocation = clinic?.availableLocations?.find(loc => loc.ID === clinic?.locationId);

    return assignedLocation?.phone || clinic?.phone || effectiveLocationData?.phone || null;
  }, [portalPatient?.clinic, effectiveLocationData?.phone]);

  // Friendly label for the picked location, used in the "done" summary
  // of Step 1 once the user has chosen.
  const selectedLocationSummary = useMemo(() => {
    if (!selectedLocationPath) return '';
    const loc = availableLocations.find(l => l.path === selectedLocationPath);

    if (!loc) return '';

    const cityState = [loc.city, loc.state].filter(Boolean).join(', ');

    return loc.name || [loc.address1, cityState].filter(Boolean).join(' · ') || 'Location';
  }, [availableLocations, selectedLocationPath]);

  if (cannotBook) return <NotSetup phone={notSetupPhone} />;
  // Don't gate the Location step on having a location loaded — the
  // picker uses lightweight metadata from the portal context, so it
  // can render before any /location/{path} fetch resolves.
  if (!effectiveLocationData && !(isMultiLocation && !selectedLocationPath)) {
    return <ScheduleSkeleton />;
  }

  // Shared variant-level summary state — computed once, reused across
  // every option's header / progress / summary chrome.
  const primaryColor = effectiveLocationData?.primaryColor || '#77bd43';

  // Post-booking confirmation. Renders inline instead of navigating
  // away so the bottom nav stays put and the user can immediately
  // jump to their appointments or book another without a round-trip.
  if (bookedAppointment) {
    return (
      <div className="flex flex-col gap-6 py-4 max-w-2xl mx-auto">
        <div className="flex flex-col items-center text-center">
          <div
            className="flex h-16 w-16 items-center justify-center rounded-2xl text-white mb-5"
            style={{ backgroundColor: primaryColor }}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.5}
              className="h-8 w-8"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 12.75l4.5 4.5 9-10.5" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
            Appointment scheduled
          </h1>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 max-w-sm">
            We&apos;ll see you then. A confirmation has been sent to you.
          </p>
        </div>

        <section className="portal-card p-4 flex flex-col gap-3">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
            Appointment summary
          </h3>
          <div className="flex items-center gap-3">
            {bookedAppointment.providerImage ? (
              <img
                src={bookedAppointment.providerImage}
                alt={bookedAppointment.providerName}
                className="h-10 w-10 rounded-xl object-cover"
              />
            ) : (
              <span
                className="inline-flex h-10 w-10 items-center justify-center rounded-xl text-white font-bold text-sm"
                style={{ backgroundColor: primaryColor }}
              >
                {bookedAppointment.providerName?.charAt(0)?.toUpperCase() || 'P'}
              </span>
            )}
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                {bookedAppointment.providerName}
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500 truncate">
                {bookedAppointment.treatmentName}
              </p>
            </div>
          </div>
          <div className="flex flex-col gap-2 text-sm">
            <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
              <CalendarDaysIcon
                className="h-5 w-5 text-gray-300 dark:text-gray-600 shrink-0"
                aria-hidden="true"
              />
              <p>
                {bookedAppointment.appointmentDate}
                {bookedAppointment.appointmentTime && (
                  <>
                    <span className="text-gray-300 dark:text-gray-600 mx-2">·</span>
                    {bookedAppointment.appointmentTime}
                  </>
                )}
              </p>
            </div>
            {bookedAppointment.patientName && (
              <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                <UserIcon
                  className="h-5 w-5 text-gray-300 dark:text-gray-600 shrink-0"
                  aria-hidden="true"
                />
                <p className="text-xs">For {bookedAppointment.patientName}</p>
              </div>
            )}
          </div>
          {bookedAppointment.location && bookedAppointment.startTimestamp > 0 && (
            <button
              type="button"
              onClick={async () => {
                const ok = await downloadAppointmentIcs({
                  appointmentId: bookedAppointment.appointmentId,
                  startTimestamp: bookedAppointment.startTimestamp,
                  durationMinutes: bookedAppointment.durationMinutes,
                  providerName: bookedAppointment.providerName,
                  treatmentName: bookedAppointment.treatmentName,
                  patientName: bookedAppointment.patientName,
                  location: bookedAppointment.location!,
                });
                if (!ok) {
                  clearTimeout(calendarToastTimer.current);
                  setCalendarToast(true);
                  calendarToastTimer.current = setTimeout(() => setCalendarToast(false), 5000);
                }
              }}
              className="mt-1 self-start inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-semibold ring-1 ring-inset ring-gray-200 dark:ring-white/10 text-gray-700 dark:text-gray-300 transition-colors active:bg-gray-50 dark:active:bg-white/5"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              Add to calendar
            </button>
          )}
          <InlineToast
            show={calendarToast}
            variant="error"
            icon={<ExclamationTriangleIcon className="h-4 w-4" />}
          >
            Unable to add to calendar. If you are using an in-app browser, try opening your browser
            instead.
          </InlineToast>
        </section>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => {
              // Public flow drops the user into the authed portal at `/`
              // — they're now signed in (the Login fallback OTP'd them
              // during confirm), so RootLanding's auth check will
              // resolve their patient and Container will mount with
              // their selected clinic. From there they can navigate
              // to Appointments themselves.
              navigate(isPublic ? '/' : '/appointments');
            }}
            className="flex-1 inline-flex items-center justify-center rounded-full py-3 text-sm font-semibold text-white transition-opacity active:opacity-80"
            style={{ backgroundColor: primaryColor }}
          >
            {isPublic ? 'Go to account' : 'View appointments'}
          </button>
          <button
            type="button"
            onClick={() => setBookedAppointment(null)}
            className="flex-1 inline-flex items-center justify-center rounded-full py-3 text-sm font-semibold border border-gray-950/10 dark:border-white/15 text-gray-900 dark:text-gray-100 transition-colors active:bg-gray-50 dark:active:bg-white/5"
          >
            Book another
          </button>
        </div>
      </div>
    );
  }

  const hasLocation = !isMultiLocation || !!selectedLocationPath;
  const hasTreatment = !!values.treatment;
  const hasProvider = !!values.provider;
  const hasTimeSlot = !!values.startTime;
  const providerName =
    values.provider === 'no-preference' ? 'Any available provider' : providerSelected?.name || '';
  const dateDisplay = values?.date
    ? dayjs.tz(values.date, effectiveLocationData?.timezone).format('ddd, MMM D')
    : '';

  const confirmStep = values.startTime ? (
    <ScheduleConfirm
      values={values}
      setValues={setValues}
      onChange={(key: string) => (value: string) => {
        setValues((prev: any) => ({
          ...prev,
          [key]: value,
        }));
      }}
      treatmentName={treatmentName}
      providerName={providerSelected?.name || ''}
      providerImage={providerSelected?.profileImage || ''}
      appointmentDate={dayjs
        .tz(values?.date, effectiveLocationData?.timezone)
        .format('MMMM D, YYYY')}
      appointmentTime={appointmentTime}
      makeChanges={() => {
        setError(null);
        setAvailability({});
        setValues((prev: any) => ({
          ...prev,
          date: null,
          startTime: null,
          endTime: null,
        }));
        scrollToId('time');
      }}
      setUser={setUser}
      path={effectivePath}
      getMe={getMe}
      me={me}
      confirm={confirm}
      isSaving={isSaving}
      error={error}
      locationData={effectiveLocationData as HealthComLocation}
      isCreditCardRequired={isCreditCardRequired}
      epayPublicKey={epayPublicKey}
      processor={processor}
      dejavooEcommToken={dejavooEcommToken}
      dejavooCardEntryRef={dejavooCardEntryRef}
      hasPhoneConsent={hasPhoneConsent}
      isPublic={isPublic}
      verifiedGuest={verifiedGuest}
      onVerifiedGuestChange={setVerifiedGuest}
      loginOnAuthSuccess={
        isPublic
          ? () => {
              // Public flow: stay on the confirm screen after the
              // embedded Login finishes. setUser/getMe inside Login
              // have already populated the parent's auth state, so
              // this view will re-render with the patient picker /
              // new-patient form and the user can click Book.
            }
          : undefined
      }
    />
  ) : null;

  // Each step is a separate view so variants can decide whether to stack
  // them all, show only the active one, or render them inside wrappers
  // with custom chrome. Wrapping each in a scroll-target anchor so the
  // sticky header doesn't cover them when we `scrollIntoView` after a
  // selection (offset comes from CSS `scroll-margin-top` in index.css).
  const locationsView = isMultiLocation && (
    <div id="step-location">
      <ScheduleLocations
        locations={availableLocations}
        value={selectedLocationPath}
        onChange={(newPath: string) => {
          // Switching locations invalidates everything past Step 1, so
          // wipe treatment/provider/time and any in-flight availability.
          setAvailability({});
          setAvailableTimes([]);
          setSelectedDay(undefined);
          setValues((prev: any) => ({
            ...prev,
            treatment: null,
            provider: null,
            date: null,
            startTime: null,
            endTime: null,
          }));
          setSelectedLocationPath(newPath);
          scrollToId('step-treatment');
        }}
      />
    </div>
  );

  const treatmentsView = (
    <div id="step-treatment">
      <ScheduleTreatments
        disciplines={effectiveLocationData?.disciplines || []}
        value={values.treatment}
        onChange={(treatment: number) => {
          setAvailability({});
          setValues((prev: any) => ({
            ...prev,
            treatment,
            provider: null,
            date: null,
            startTime: null,
            endTime: null,
          }));
          scrollToId('step-provider');
        }}
        providerOptions={providerOptions}
      />
    </div>
  );

  const providersView = hasTreatment && (
    <div id="step-provider">
      <ScheduleProviders
        providers={providerOptions}
        value={values.provider}
        onChange={async (provider: string) => {
          setAvailability({});
          setAvailableTimes([]);
          setValues((prev: any) => ({
            ...prev,
            provider,
            date: null,
            startTime: null,
            endTime: null,
          }));
          scrollToId('step-time');
          await getAvailability(
            provider,
            values.treatment,
            viewDate.format('YYYY'),
            viewDate.format('MM')
          );
        }}
      />
    </div>
  );

  const timeView = hasProvider && (
    <div id="step-time">
      <ScheduleTime
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        className="mt-8"
        onChange={(val: any) => {
          setValues((prev: any) => ({
            ...prev,
            ...val,
          }));
        }}
        primaryColor={effectiveLocationData?.primaryColor || primaryColor}
        onSelectDate={(date?: string) => {
          if (!date) return setAvailableTimes([]);

          const availabilityForDay = availability?.[date];

          const availabilityForProvider =
            values?.provider && values?.provider !== 'no-preference'
              ? [
                  {
                    providerId: values?.provider,
                    providerName: providerInfo(values?.provider)?.name,
                    profileImage: providerInfo(values?.provider)?.profileImage,
                    times: availabilityForDay?.[values?.provider] || [],
                  },
                ]
              : Object.entries(availabilityForDay || {}).map(([provider, times]) => ({
                  providerId: provider,
                  providerName: providerInfo(provider)?.name,
                  profileImage: providerInfo(provider)?.profileImage,
                  times: times || [],
                }));

          setAvailableTimes(availabilityForProvider);
          setSelectedDay(dayjs(date).format('MMMM D, YYYY'));
          scrollToId('step-time');
        }}
        selectedDay={selectedDay}
        onChangeMonth={(year: string, month: string) => {
          getAvailability(values.provider, values.treatment, year, month);
        }}
        dottedDays={dottedDays}
        loading={loadingAvailability}
        availableTimes={availableTimes}
        setViewDate={setViewDate}
        viewDate={viewDate}
      />
    </div>
  );

  // "Go back and edit this step" — clearing the step's value (and anything
  // forward of it) naturally makes that step's view visible again because
  // the card stack only shows a step's body when its state is 'active'.
  // Step keys are semantic (location/treatment/provider/time) instead of
  // numeric, since the visible step numbers shift by one when a clinic
  // has multiple locations.
  const editStep = (step: 'location' | 'treatment' | 'provider' | 'time') => {
    setAvailability({});
    setAvailableTimes([]);
    setSelectedDay(undefined);

    if (step === 'location') {
      setSelectedLocationPath(null);
      setValues((prev: any) => ({
        ...prev,
        treatment: null,
        provider: null,
        date: null,
        startTime: null,
        endTime: null,
      }));
      scrollToId('step-location');

      return;
    }

    if (step === 'treatment') {
      setValues((prev: any) => ({
        ...prev,
        treatment: null,
        provider: null,
        date: null,
        startTime: null,
        endTime: null,
      }));
      scrollToId('step-treatment');
    } else if (step === 'provider') {
      setValues((prev: any) => ({
        ...prev,
        provider: null,
        date: null,
        startTime: null,
        endTime: null,
      }));
      scrollToId('step-provider');
    } else {
      setValues((prev: any) => ({
        ...prev,
        date: null,
        startTime: null,
        endTime: null,
      }));
      scrollToId('step-time');
    }
  };

  return (
    <div className="flex flex-col gap-6">
      {/* In public mode the surrounding shell already shows a "Book a
          visit" heading next to the clinic logo, so suppressing this
          one avoids the duplicate page title. The authed portal
          (mode='portal') keeps it because /schedule lives inside
          Container which doesn't have its own page heading.
          Suppressed on the confirm step too — ScheduleConfirm renders
          its own "You're almost booked" heading and we don't want
          two competing page titles. */}
      {!isPublic && !confirmStep && (
        <div>
          <p
            className="text-xs font-semibold uppercase tracking-wider"
            style={{ color: primaryColor }}
          >
            Schedule
          </p>
          <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100 mt-1">
            Book a visit
          </h1>
        </div>
      )}

      {confirmStep ?? (
        <ol role="list" className="flex flex-col">
          {(() => {
            type WizardStep = {
              key: 'location' | 'treatment' | 'provider' | 'time';
              label: string;
              state: 'active' | 'done' | 'locked';
              summary: string;
              body: React.ReactNode;
              onEdit: () => void;
            };

            const steps: WizardStep[] = [];

            if (isMultiLocation) {
              steps.push({
                key: 'location',
                label: 'Location',
                state: hasLocation ? 'done' : 'active',
                summary: selectedLocationSummary,
                body: !hasLocation ? locationsView : null,
                onEdit: () => editStep('location'),
              });
            }

            steps.push({
              key: 'treatment',
              label: 'Treatment',
              state: !hasLocation ? 'locked' : hasTreatment ? 'done' : 'active',
              summary: treatmentName,
              body: hasLocation && !hasTreatment ? treatmentsView : null,
              onEdit: () => editStep('treatment'),
            });

            steps.push({
              key: 'provider',
              label: 'Provider',
              state: !hasLocation || !hasTreatment ? 'locked' : hasProvider ? 'done' : 'active',
              summary: providerName,
              body: hasLocation && hasTreatment && !hasProvider ? providersView : null,
              onEdit: () => editStep('provider'),
            });

            steps.push({
              key: 'time',
              label: 'Time',
              state:
                !hasLocation || !hasTreatment || !hasProvider
                  ? 'locked'
                  : hasTimeSlot
                    ? 'done'
                    : 'active',
              summary: dateDisplay
                ? `${dateDisplay}${appointmentTime ? ` · ${appointmentTime}` : ''}`
                : '',
              body: hasLocation && hasProvider && !hasTimeSlot ? timeView : null,
              onEdit: () => editStep('time'),
            });

            return steps.map((step, i) => {
              const isDone = step.state === 'done';
              const isActive = step.state === 'active';
              const isLocked = step.state === 'locked';

              return (
                <li
                  key={step.key}
                  className={cn(
                    'border-t border-gray-950/10 dark:border-white/10 py-6 sm:py-8',
                    i === steps.length - 1 && 'border-b',
                    isLocked && 'opacity-40'
                  )}
                >
                  {/*
                   * 2-col grid: the big step number occupies row 1 col 1,
                   * the heading + Edit row occupies row 1 col 2. The
                   * summary/body (which is the bulky bit — the picker
                   * / calendar / time list) sits on row 2 and spans the
                   * full width on mobile so it uses the otherwise-empty
                   * space under the number; on sm+ it stays in col 2
                   * to keep the indented look the desktop already had.
                   */}
                  <div className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 sm:gap-x-10">
                    <span
                      className="text-4xl sm:text-5xl font-semibold tabular-nums tracking-tight leading-none shrink-0"
                      style={{
                        color: isActive || isDone ? primaryColor : 'rgb(156 163 175)',
                      }}
                    >
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <div className="flex items-baseline justify-between gap-4">
                      <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-gray-900 dark:text-gray-100">
                        {step.label}
                      </h2>
                      {isDone && (
                        <button
                          type="button"
                          onClick={step.onEdit}
                          className="text-sm font-semibold shrink-0"
                          style={{ color: primaryColor }}
                        >
                          Edit
                        </button>
                      )}
                    </div>
                    {isDone && step.summary && (
                      <p className="mt-1 col-span-2 sm:col-span-1 sm:col-start-2 text-base text-gray-500 dark:text-gray-400">
                        {step.summary}
                      </p>
                    )}
                    {isActive && step.body && (
                      <div className="mt-5 sm:mt-6 col-span-2 sm:col-span-1 sm:col-start-2">
                        {step.body}
                      </div>
                    )}
                  </div>
                </li>
              );
            });
          })()}
        </ol>
      )}
    </div>
  );
};

export default Schedule;
